/**
 * Strips conversational preambles, residual code fences, and whole-string
 * wrapping quotes from a plain-text AI response. Applied AFTER stripMarkdown
 * in getByQueryChat for non-schema (string) responses, as a defense-in-depth
 * cleaner for drivers that can't be forced into structured output
 * (company own-key Claude, etc.).
 *
 * Conservative by design:
 *  - Preambles are only removed when anchored to the start AND ending in a
 *    colon AND matching a known lead-in vocabulary ("Here is ...:",
 *    "Translation:", ...), so mid-sentence colons are never touched.
 *  - Wrapping quotes are only stripped when the ENTIRE string is wrapped,
 *    so a legitimate quote inside the text is preserved.
 */
export const cleanAIText = (text) => {
    if (!text || typeof text !== "string") return text || "";

    let t = text.trim();

    // Residual code fences (stripMarkdown usually handles these; defensive).
    t = t.replace(/^```(?:json|text|markdown)?\s*/i, "").replace(/\s*```$/i, "").trim();

    // Leading conversational preamble: "Sure, here's the translation:",
    // "Here is the result:", "Translated text:", "Output:", etc.
    t = t
        .replace(
            /^(?:sure[,!.]?\s*)?(?:here(?:'s| is| are)[^:\n]{0,60}:|(?:the\s+)?(?:translation|translated text|result|output|answer|rewrite|rewritten text|revised text|corrected text|response)\s*:)\s*/i,
            ""
        )
        .trim();

    // Whole-string wrapping quotes (straight or curly), single or double.
    const pairs = [
        ['"', '"'],
        ["'", "'"],
        ["“", "”"], // “ ”
        ["‘", "’"], // ‘ ’
        ["«", "»"], // « »
    ];
    for (const [open, close] of pairs) {
        if (t.length >= 2 && t.startsWith(open) && t.endsWith(close)) {
            const inner = t.slice(1, -1);
            // Only strip when there's no unbalanced same-quote inside (for same
            // open/close pairs); asymmetric pairs are always safe to strip.
            if (open !== close || !inner.includes(open)) {
                t = inner.trim();
                break;
            }
        }
    }

    return t.trim();
};

export default cleanAIText;
